import { FormSkeleton } from "components/new-stuff/ui/Skeleton"
import { DatePicker, Drawer, Form } from "antd"
import { Button } from "components"
import { CustomInput, CustomSelect } from "components/new-stuff/form"
import { CloseOutlined } from "@ant-design/icons"
import { newArchAxiosInstance } from "utils/request"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useAppNotificationStore } from "stores"
import { onQueryError } from "utils/utils"
import { useGet } from "utils/useFetch"
import dayjs from "dayjs"
import {
  useCycleDurationID,
  useCycleDurationAdminStore,
  useCycleDurationEmpStore,
  useCycleDurationManagerStore,
} from "stores/cycle-duration"
import { CustomSpin } from "components/custom/Loader"
import { IPerfEmployees } from "pages/dashboard/performance/okrs/interface/enums"
import { IPeople } from "pages/dashboard/performance/types"
import {
  ICompObjListing,
  IPerson,
  IDptListing,
  IDptObjResp,
  IEnums,
  ISingleEmployeeKRResp,
} from "./types"
import MultipleObjInfoCard from "../../okrs/component/MultipleObjInfoCard"
import { useState } from "react"
import BulkUploadObj from "../../okrs/BulkUploadObj"

interface props {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  krId?: number | null
  deptId?: number | null
  deptObjId?: number | null
  employeeId?: number | null
  role: "admin" | "employee" | "manager"
}

export const CreateEditKR = ({
  open,
  onClose,
  onSuccess,
  krId,
  role,
  deptId,
  deptObjId,
  employeeId,
}: props) => {
  const [form] = Form.useForm()
  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()
  const [openBulkUpload, setOpenBulkUpload] = useState(false)

  const cdId = useCycleDurationID({ type: role })

  const selectedCDDataAdmin = useCycleDurationAdminStore(
    (state) => state.selectedCDData
  )
  const selectedCDDataEmp = useCycleDurationEmpStore(
    (state) => state.selectedCDData
  )
  const selectedCDDataManager = useCycleDurationManagerStore(
    (state) => state.selectedCDData
  )

  const selectedCDData =
    role === "admin"
      ? selectedCDDataAdmin
      : role === "employee"
      ? selectedCDDataEmp
      : selectedCDDataManager

  const isPersonCentric =
    selectedCDData?.frameworkType === 2 ||
    selectedCDData?.frameworkTypeDescription === "PersonCentered"

  const selectedMetric = Form.useWatch("metric", form)
  const selectedDptId = Form.useWatch("departmentId", form)
  const selectedDptObjId = Form.useWatch("departmentObjectiveId", form)
  const selectedCompanyObjId = Form.useWatch("companyObjectiveId", form)

  const { isLoading: isLoadingProfile, data: profile } = useGet<IPerson>({
    url: `/employee/details/view/index_personal_info`,
    instanceKey: "newArchAxiosInstance",
    onSuccess: (data: IPerson) => {
      form.setFieldsValue({
        departmentId: data?.data?.department_id,
      })
      if (role === "employee") {
        form.setFieldsValue({
          employee: data?.data?.person_id,
        })
      }
    },
    enabled: open && (role === "employee" || role === "manager"),
  })

  const dptId =
    role === "employee" || role === "manager"
      ? profile?.data?.department_id
      : deptId

  const { isLoading: isLoadingDpt, data: dptListing } = useGet<IDptListing>({
    url:
      role === "manager"
        ? `/api/v2/manager/okr/departments`
        : `/api/v2/${role}/performance/departments`,
    instanceKey: "newArchAxiosInstance",
    // enabled: role !== "employee",
  })

  const { isLoading: isLoadingDptObj, data: dptObjListing } =
    useGet<IDptObjResp>({
      url:
        role === "employee"
          ? `/api/v2/employee/okr/departmentobjectives/${selectedDptId}/${cdId}`
          : role === "manager"
          ? `/api/v2/manager/okr/departmentobjectives?cycleDurationId=${cdId}`
          : `/api/v2/admin/okr/departmentobjectives/${selectedDptId}?cycleDurationId=${cdId}`,
      instanceKey: "newArchAxiosInstance",
      enabled: !!selectedDptId && !!cdId,
    })

  const { isLoading: isLoadingCompObj, data: companyObjListing } =
    useGet<ICompObjListing>({
      url: `/api/v2/${role}/okr/companyobjectives?cycleDurationId=${cdId}&status=1`,
      instanceKey: "newArchAxiosInstance",
      enabled: !!cdId && isPersonCentric,
    })

  const { isLoading: isLoadingEnum, data: enums } = useGet<IEnums>({
    url: `/api/v2/admin/enums`, // endpoint not gated, so all roles can call
    instanceKey: "newArchAxiosInstance",
  })

  const { isLoading: isPeopleLoading, data: peopleData } = useGet<IPeople>({
    url: "/api/v2/admin/performance/people",
    instanceKey: "newArchAxiosInstance",
    enabled: role !== "employee" && !selectedDptId,
  })

  const { isLoading: isLoadingEmployees, data: employees } =
    useGet<IPerfEmployees>({
      url: `/api/v2/${
        role === "admin" ? "admin" : "employee"
      }/performance/members?departmentId=${selectedDptId}`,
      instanceKey: "newArchAxiosInstance",
      enabled: !!selectedDptId,
    })

  const { isFetching: isLoadingDetails, data: keyResultDetails } =
    useGet<ISingleEmployeeKRResp>({
      url: `/api/v2/${role}/okr/keyresults/targets/${krId}`,
      instanceKey: "newArchAxiosInstance",
      onSuccess: (data: ISingleEmployeeKRResp) => {
        form.setFieldsValue({
          keyResultTitle: data?.data?.keyResultTitle,
          departmentId: data?.data?.departmentId,
          departmentObjectiveId: data?.data?.departmentObjectiveId,
          companyObjectiveId: (data as any)?.data?.companyObjectiveId,
          metric: data?.data?.metric,
          weight: data?.data?.weight,
          employee: data?.data?.employeeTargetDto?.[0]?.employeeId,
          start: data?.data?.employeeTargetDto?.[0]?.startValue,
          end: data?.data?.employeeTargetDto?.[0]?.endValue,
          amountMetric: data?.data?.amountMetric,
          due_date: data?.data?.employeeTargetDto?.[0]?.dueDate
            ? dayjs(data?.data?.employeeTargetDto?.[0]?.dueDate)
            : null,
        })
      },
      enabled: !!krId,
    })

  const { isLoading: isSubmitting, mutate: submit } = useMutation({
    mutationFn: async (body: Payload) => {
      const isEdit = !!krId // only admin and manager can edit
      const personCentricRole = role
      // /api/v{version}/manager/okr/employeekeyresults/{employeeKeyResultId}
      // /api/v{version}/employee/okr/keyresults/{employeeKeyResultId}
      // /api/v{version}/admin/okr/employeekeyresults/{employeeKeyResultId}
      // const endpoint =
      //   role === "employee"
      //     ? `/api/v2/employee/okr/keyresult`
      //     : isEdit
      //     ? `/api/v2/${role}/okr/department/employeekeyresult/${krId}`
      //     : `/api/v2/${role}/okr/department/employeekeyresult`

      const endpoint = isPersonCentric
        ? isEdit
          ? `/api/v2/${personCentricRole}/okr/okrframework/employeekeyresults/${krId}`
          : `/api/v2/${personCentricRole}/okr/okrframework/employeekeyresults`
        : isEdit
        ? role === "employee"
          ? `/api/v2/employee/okr/keyresults/${krId}`
          : `/api/v2/${role}/okr/employeekeyresults/${krId}`
        : role === "employee"
        ? `/api/v2/employee/okr/keyresult`
        : `/api/v2/${role}/okr/department/employeekeyresult`

      const method = isEdit ? "patch" : "post"

      const res = await newArchAxiosInstance.request({
        url: endpoint,
        method,
        data: body,
      })
      return res.data
    },
    onSuccess: (data) => {
      if (data.success) {
        if (role === "employee") {
          toast.success("Key Result submitted for approval")
        } else {
          toast.success(data.message || "Key Result successfully saved")
        }
        onClose()
        onSuccess?.()
        queryClient.invalidateQueries({
          queryKey: ["/api/v2/admin/okr/departmentobjectives"],
        })
        queryClient.invalidateQueries({
          queryKey:
            role === "employee"
              ? [`/api/v2/employee/okr/departmentobjectives/${cdId}`]
              : role === "manager"
              ? [
                  `/api/v2/manager/okr/departmentobjectives/keyresults/${selectedDptObjId}`,
                ]
              : [`/api/v2/admin/okr/departmentkeyresults/${selectedDptObjId}`],
        })
      }
    },
    onError: onQueryError,
  })

  const onFinish = (values: Values) => {
    const adminAndManagerPayload: Payload = {
      keyResultTitle: values?.keyResultTitle,
      departmentObjectiveId: Number(values?.departmentObjectiveId),
      departmentId: Number(values?.departmentId),
      metric: Number(values?.metric),
      weight:
        values?.weight !== undefined &&
        values?.weight?.toString()?.trim() !== ""
          ? Number(values?.weight)
          : null,
      amount: Number(values?.amountMetric) ?? 0,
      cycleDurationId: Number(cdId),
      employeeTargetRequest: [
        {
          employeeId: Number(values.employee),
          startValue: Number(values.start ?? 0),
          endValue: Number(values.end ?? 0),
          dueDate: dayjs(values.due_date).format("YYYY-MM-DD"),
        },
      ],
    }

    const employeePayload: Payload = {
      keyResultTitle: values?.keyResultTitle,
      departmentObjectiveId: Number(values?.departmentObjectiveId),
      departmentId: Number(values?.departmentId),
      metric: Number(values?.metric),
      amount: Number(values?.amountMetric) ?? 0,
      weight:
        values?.weight !== undefined &&
        values?.weight?.toString()?.trim() !== ""
          ? Number(values?.weight)
          : null,
      dueDate: dayjs(values.due_date).format("YYYY-MM-DD"),
      startValue: Number(values.start ?? 0),
      endValue: Number(values.end ?? 0),
    }

    const personCentricAdminOrManagerPayload: Payload = {
      employeeId: Number(values.employee),
      endValue: Number(values.end ?? 0),
      keyResultTitle: values?.keyResultTitle,
      metric: Number(values?.metric),
      startValue: Number(values.start ?? 0),
      departmentObjectiveId: values?.departmentObjectiveId
        ? Number(values.departmentObjectiveId)
        : null,
      departmentId: values?.departmentId ? Number(values.departmentId) : null,
      companyObjectiveId: values?.companyObjectiveId
        ? Number(values.companyObjectiveId)
        : null,
      amount: Number(values?.amountMetric) ?? 0,
      weight:
        values?.weight !== undefined &&
        values?.weight?.toString()?.trim() !== ""
          ? Number(values?.weight)
          : null,
      cycleDurationId: Number(cdId),
      dueDate: dayjs(values.due_date).format("YYYY-MM-DD"),
    }

    const personCentricEmployeePayload: Payload = {
      endValue: Number(values.end ?? 0),
      keyResultTitle: values?.keyResultTitle,
      metric: Number(values?.metric),
      startValue: Number(values.start ?? 0),
      departmentObjectiveId: values?.departmentObjectiveId
        ? Number(values.departmentObjectiveId)
        : null,
      departmentId: values?.departmentId ? Number(values.departmentId) : null,
      companyObjectiveId: values?.companyObjectiveId
        ? Number(values.companyObjectiveId)
        : null,
      amount: Number(values?.amountMetric) ?? 0,
      weight:
        values?.weight !== undefined &&
        values?.weight?.toString()?.trim() !== ""
          ? Number(values?.weight)
          : null,
      cycleDurationId: Number(cdId),
      dueDate: dayjs(values.due_date).format("YYYY-MM-DD"),
    }

    const payload = isPersonCentric
      ? role === "employee"
        ? personCentricEmployeePayload
        : personCentricAdminOrManagerPayload
      : role === "employee"
      ? employeePayload
      : adminAndManagerPayload

    submit(payload)
  }

  const peopleOptions = (
    selectedDptId ? employees?.data?.docs : peopleData?.data?.docs
  )?.map((emp) => ({
    label: emp.fullName,
    value: emp.personId,
  }))

  return (
    <Drawer
      width={675}
      title={
        <span className=" text-padeBlack font-circular text-xl">
          {krId ? "Edit" : "Add"} Key Result
        </span>
      }
      open={open}
      onClose={onClose}
      closeIcon={false}
      className="bg-white relative"
      extra={
        <button onClick={onClose}>
          <CloseOutlined className="h-6 w-6" />
        </button>
      }
      footer={
        <div className="flex justify-end gap-4 ">
          <Button
            color="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            color="primary"
            onClick={() => form.submit()}
            submitting={isSubmitting}
          >
            {krId ? "Update" : "Create"}
          </Button>
        </div>
      }
    >
      <CustomSpin
        placeholder={
          <FormSkeleton
            header={false}
            fields={2}
          />
        }
        spinning={
          role === "employee" || role === "manager"
            ? isLoadingProfile
            : krId
            ? isLoadingDetails
            : false
        }
      >
        <div className="">
          {!krId && role !== "employee" && (
            <MultipleObjInfoCard
              type="individual"
              onClick={() => cdId && setOpenBulkUpload(true)}
            />
          )}
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              departmentId: dptId,
              departmentObjectiveId: deptObjId,
              employee: employeeId,
            }}
          >
            <div>
              <CustomInput
                label="Key Result "
                name="keyResultTitle"
                required
                placeholder="E.g Increase customer retention rate from 70% to 85%"
              />

              <CustomSelect
                label="Department"
                name="departmentId"
                required={!isPersonCentric}
                isLoading={isLoadingDpt}
                placeholder="Select Department"
                options={dptListing?.data?.docs?.map((dpt) => ({
                  label: dpt.nameOfDepartment,
                  value: dpt.departmentId,
                }))}
                showSearch
                disabled={
                  role === "employee" ||
                  role === "manager" ||
                  (role === "admin" && !!deptId)
                }
                onChange={() => {
                  // reset departmentObjectiveId
                  form.setFieldsValue({ departmentObjectiveId: undefined })
                }}
              />

              <CustomSelect
                label="Align with Department Objective"
                name="departmentObjectiveId"
                required={!isPersonCentric}
                isLoading={isLoadingDptObj}
                options={(isPersonCentric && selectedCompanyObjId
                  ? dptObjListing?.data?.filter(
                      (obj) => obj.companyObjectiveId === selectedCompanyObjId
                    )
                  : dptObjListing?.data
                )?.map((obj) => ({
                  label: obj.departmentObjectiveName,
                  value: obj.departmentObjectiveId,
                }))}
              />

              {isPersonCentric && (
                <CustomSelect
                  label="Align with Company Goal"
                  name="companyObjectiveId"
                  required={false}
                  isLoading={isLoadingCompObj}
                  placeholder="Select Company Goal (Optional)"
                  options={companyObjListing?.data?.map((obj) => ({
                    label: obj.objectiveName,
                    value: obj.companyObjectiveId,
                  }))}
                  onChange={() => {
                    form.setFieldsValue({ departmentObjectiveId: undefined })
                  }}
                />
              )}

              <CustomSelect
                label="What kind of metric will be used to measure it?"
                name="metric"
                required
                isLoading={isLoadingEnum}
                placeholder="Select metric"
                options={enums?.data?.Metric
                  //   ?.filter(
                  //   (met) => met.id !== 3
                  // )
                  ?.map((metric) => ({
                    label: metric.name,
                    value: metric.id,
                  }))}
              />
              {(() => {
                const target = keyResultDetails?.data?.employeeTargetDto?.[0]
                const hasProgress =
                  target != null &&
                  Number(target.currentProgress ?? 0) >
                    Number(target.startValue ?? 0)
                const metricChanged =
                  keyResultDetails?.data?.metric != null &&
                  selectedMetric != null &&
                  Number(selectedMetric) !==
                    Number(keyResultDetails.data.metric)
                if (!krId || !metricChanged || !hasProgress) return null
                return (
                  <div className="-mt-4 mb-4 rounded-md border border-[#F5C97A] bg-[#FFF7E6] px-3 py-2 font-avenir text-xs text-[#92660A]">
                    Changing the metric will reset the progress of this key
                    result.
                  </div>
                )
              })()}

              <CustomInput
                label={"Assign Weight (%)"}
                name="weight"
                placeholder="Leave blank for auto-distribution"
                extra={
                  <span className="text-sm text-padeSubtext font-avenir">
                    Leave blank to auto-distribute weight. Enter 0 to track
                    without affecting objective score.
                  </span>
                }
              />

              {selectedMetric == 2 && (
                <CustomInput
                  label={"Amount Metric"}
                  name="amountMetric"
                  required
                  placeholder="E.g 50"
                />
              )}

              <CustomSelect
                label={"Employee"}
                name="employee"
                disabled={role === "employee" || !!employeeId}
                required
                placeholder={role === "employee" ? "" : "Select employee"}
                options={peopleOptions}
                isLoading={selectedDptId ? isLoadingEmployees : isPeopleLoading}
              />

              <>
                {selectedMetric !== 3 && selectedMetric !== 2 && (
                  <div className="grid grid-cols-2 gap-6">
                    <CustomInput
                      label="Start Value"
                      name="start"
                      required
                      placeholder="E.g 20"
                    />
                    <CustomInput
                      label="End Value"
                      name="end"
                      required
                      placeholder="E.g 200"
                    />
                  </div>
                )}

                <Form.Item
                  name="due_date"
                  className="w-full flex gap-2 flex-col"
                  label={
                    <span className="text-padeBlack font-circular text-sm">
                      Due Date
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please enter this field" },
                  ]}
                >
                  <DatePicker
                    className="w-full"
                    placeholder="Date"
                    disabledDate={(date) => {
                      const endDate = dayjs()
                      const afterEndDate = date.isBefore(endDate)
                      return afterEndDate
                    }}
                  />
                </Form.Item>
              </>
            </div>
          </Form>

          <BulkUploadObj
            open={openBulkUpload}
            handleClose={() => setOpenBulkUpload(false)}
            selectedCycle={cdId ? Number(cdId) : null}
            handleOnSuccess={() => {
              setOpenBulkUpload(false)
              queryClient.invalidateQueries({
                queryKey:
                  role === "employee"
                    ? [`/api/v2/employee/okr/departmentobjectives/${cdId}`]
                    : role === "manager"
                    ? [
                        `/api/v2/manager/okr/departmentobjectives/keyresults/${selectedDptObjId}`,
                      ]
                    : [
                        `/api/v2/admin/okr/departmentkeyresults/${selectedDptObjId}`,
                      ],
              })
              onClose()
            }}
            type={"individual"}
            deptId={selectedDptId}
            role={role}
            isPersonCentric={isPersonCentric}
          />
        </div>
      </CustomSpin>
    </Drawer>
  )
}

interface Values {
  keyResultTitle: string
  departmentObjectiveId?: number
  departmentId?: number
  companyObjectiveId?: number
  metric: number
  amountMetric: number
  weight: number
  employee: number
  start: number
  end: number
  due_date: string
}

interface Payload {
  employeeId?: number
  companyId?: number | null
  keyResultTitle: string
  departmentObjectiveId: number | null
  departmentId: number | null
  companyObjectiveId?: number | null
  metric: number | null
  amount: number
  weight: number | null
  dueDate?: string
  startValue?: number
  endValue?: number
  cycleDurationId?: number
  employeeTargetRequest?: {
    employeeId: number
    startValue: number
    endValue: number
    dueDate: string
  }[]
}
